// The calendar page's load-bearing rules, kept pure so they can be pinned.
//
// Both came out of Jason's review of #1998, and both are the kind of thing a
// render test does not catch: a tile printing a number it does not have, and a
// comment promising a window the code beside it does not read.
import { readState, countOrDash, type ReadStateLike } from '@/lib/readState'
import { ymdWeek } from '@/lib/dates'

/** Shift a YYYY-MM-DD by n days, staying on the calendar. Local time on
 *  purpose — a bare date parsed at UTC midnight lands on the previous day
 *  west of Greenwich (the trap CLAUDE.md names). */
export function shiftYMD(ymd: string, days: number): string {
  const d = new Date(ymd + 'T00:00:00')
  d.setDate(d.getDate() + days)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/**
 * The deadline horizon the "Overdue" and "Due in 7 days" tiles count over.
 *
 * Forward: a year, as the page this replaced always showed. Back: TWO years,
 * which the old page did not do — it stopped at 30 days, so a certificate that
 * expired five weeks ago fell out of "Overdue" while still overdue. A deadline
 * that has passed is overdue however long ago. The floor exists only to keep
 * the read bounded: the horizon is the `due` + `planned` tenses only, so
 * widening it touches deadline tables, never pallets. Anything older than two
 * years is a stale record to clean up rather than a deadline to chase, and it
 * is the one thing this tile knowingly does not count.
 */
export const HORIZON = { pastDays: 730, futureDays: 365 } as const

export function horizonWindow(today: string): { from: string; to: string } {
  return {
    from: shiftYMD(today, -HORIZON.pastDays),
    to:   shiftYMD(today,  HORIZON.futureDays),
  }
}

/**
 * What a summary tile may print for a count derived from `q`.
 *
 * `countOrDash` — the repo idiom — withholds the number only once the read is
 * UNREAD (errored or paused with nothing held) and keeps a real number across
 * a failed refetch, the react-query v5 subtlety its own note warns about. It
 * deliberately leaves the first-load case to the caller: readState's contract
 * is that `loading` gets a pending affordance, not a figure. Without this
 * branch the tile reads "0 Overdue" for the first few hundred milliseconds of
 * every open — the same lie as the failed case, only shorter.
 */
export function tileValue(q: ReadStateLike, n: number): string {
  return readState(q) === 'loading' ? '…' : countOrDash(q, n)
}

/**
 * Where the agenda folds its past away (farm audit CR-18).
 *
 * The agenda answers "what is coming", yet it opened at the 1st of the month:
 * on the 23rd, "This week" began 9,364px down a phone, under 150+ rows of work
 * that had already happened. So for the CURRENT month everything before this
 * week's Monday goes behind an "Earlier this month" fold and the page opens on
 * this week. The whole current week stays out, done days included — splitting
 * at today would print "This week" twice, once on each side of the fold.
 *
 * Any month OTHER than the current one is not folded: it opens from its 1st.
 * Returns null then. The rule is the month, not how you got there — the
 * current month is folded even when `?m=` names it, which it does after any
 * day is opened (setOpenDay pins `m=`) or after paging away and back.
 */
export function agendaFoldBefore(today: string, windowFrom: string): string | null {
  if (windowFrom.slice(0, 7) !== today.slice(0, 7)) return null
  return ymdWeek(today).startYMD
}

/** Split date-sorted agenda rows at the fold. Order is kept on both sides. */
export function splitAtFold<T extends { day: string }>(
  rows: T[], foldBefore: string | null,
): { earlier: T[]; current: T[] } {
  if (!foldBefore) return { earlier: [], current: rows }
  const earlier: T[] = []
  const current: T[] = []
  for (const r of rows) (r.day < foldBefore ? earlier : current).push(r)
  return { earlier, current }
}

/**
 * D4 (U02): the compliance-class arms of calendar_events_raw are withheld by
 * the server, so a viewer they are withheld from sees a grid that looks clear.
 * This is the line that says so. It MIRRORS the arms, not the route gate:
 *   - the other compliance kinds need has_perm('compliance','view'); calibration
 *     also admits has_perm('qc','view') (mig 20261153000000, UF-08a);
 *   - training also needs current_tenant_role() ADMIN or MANAGER, or a platform
 *     admin (same file, :155-156). A platform admin holds `perms.all`.
 * `null` = say nothing: while the reads decide, or when the map is unknown
 * (fail-closed offline) — an unknown is not a statement either way.
 */
export const COMPLIANCE_WITHHELD_NOTE =
  'Some deadlines show only to people with Compliance access, and training only to managers.'

export function complianceWithheldNote(
  perms: { all?: boolean; permissions: Record<string, readonly string[]> } | null,
  tenantRole: string | null | undefined,
  pending: boolean,
): string | null {
  if (pending || !perms) return null
  if (perms.all) return null
  const hasCompliance = (perms.permissions.compliance ?? []).includes('view')
  const manager = tenantRole === 'ADMIN' || tenantRole === 'MANAGER'
  return hasCompliance && manager ? null : COMPLIANCE_WITHHELD_NOTE
}
